import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  User,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";

import { firebaseAuth } from "./services/firebase";

export type WebsiteAccessLevel =
  | "A1"
  | "A2"
  | "B1"
  | "B2"
  | "C1"
  | "C2";

export type WebsiteAccessSnapshot = {
  checked: boolean;
  loading: boolean;
  hasAnyAccess: boolean;
  hasFullBookAccess: boolean;
  hasFullCourseAccess: boolean;
  accessibleBookLevels: WebsiteAccessLevel[];
  accessibleCourseLevels: WebsiteAccessLevel[];
  activeEntitlements: string[];
  activeProducts: string[];
  source: string;
  error: string;
  lastCheckedAt: number;
};

type BackendAccessMap = Partial<
  Record<WebsiteAccessLevel | "full", boolean>
>;

type BackendAccessResponse = {
  email?: string;
  source?: string;
  has_record?: boolean;

  has_full_access?: boolean;
  has_full_course_access?: boolean;
  has_full_book_access?: boolean;

  course_access_levels?: string[];
  book_access_levels?: string[];

  accessible_levels?: string[];
  accessible_course_levels?: string[];
  accessible_book_levels?: string[];

  access_map?: BackendAccessMap;
  course_access_map?: BackendAccessMap;
  book_access_map?: BackendAccessMap;

  revenuecat_active_entitlements?: string[];
  revenuecat_active_products?: string[];
  revenuecat_error?: string | null;

  message?: string;
  error?: string;
};

type AuthChangeDetail = {
  isLoggedIn: boolean;
  username?: string;
  uid?: string;
  email?: string | null;
};

type StoredAccessCache = {
  userId: string;
  email: string;
  savedAt: number;
  access: WebsiteAccessSnapshot;
};

type AccessRequest = {
  key: string;
  promise: Promise<WebsiteAccessSnapshot>;
};

type AuthContextValue = {
  user: User | null;
  userId: string;
  userEmail: string;
  isLoggedIn: boolean;
  authLoading: boolean;
  authError: string;

  accessChecked: boolean;
  accessLoading: boolean;
  accessError: string;
  accessLastCheckedAt: number;
  accessSource: string;
  hasAnyAccess: boolean;
  hasFullBookAccess: boolean;
  hasFullCourseAccess: boolean;
  accessibleBookLevels: WebsiteAccessLevel[];
  accessibleCourseLevels: WebsiteAccessLevel[];
  activeEntitlements: string[];
  activeProducts: string[];

  login: (
    email: string,
    password: string,
  ) => Promise<boolean>;

  register: (
    email: string,
    password: string,
    displayName?: string,
  ) => Promise<boolean>;

  logout: () => Promise<void>;
  clearAuthError: () => void;

  refreshAccess: (
    forceRefresh?: boolean,
  ) => Promise<WebsiteAccessSnapshot>;

  canAccessBookLevel: (
    level: WebsiteAccessLevel,
  ) => boolean;

  canAccessCourseLevel: (
    level: WebsiteAccessLevel,
  ) => boolean;
};

const AuthContext =
  createContext<AuthContextValue | undefined>(
    undefined,
  );

const API_BASE_URL = (
  process.env.REACT_APP_BACKEND_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "https://www.api.dearlanguage.com"
).replace(/\/+$/, "");

const WEBSITE_PREMIUM_ENTITLEMENT =
  process.env.REACT_APP_REVENUECAT_FULL_WEBSITE_ENTITLEMENT_ID?.trim() ||
  "website_full_access";

const MOBILE_PREMIUM_ENTITLEMENT =
  process.env.REACT_APP_REVENUECAT_MOBILE_PREMIUM_ENTITLEMENT_ID?.trim() ||
  "premium";

const SHARED_AUTH_KEY =
  "dear_language_demo_course_auth";

const SHARED_AUTH_USER_KEY =
  "dear_language_demo_user_name";

const SHARED_AUTH_EVENT =
  "dear-language-auth-change";

const PROFILE_ACCESS_EVENT =
  "dear-language-profile-access-loaded";

const GLOBAL_ACCESS_EVENT =
  "dear-language-global-access-updated";

/**
 * One global access cache for Header, Profile, Courses,
 * Books and BookView.
 *
 * Normal navigation and normal page reloads reuse this result.
 */
const GLOBAL_ACCESS_CACHE_PREFIX =
  "dear-language-global-access-v4";

const GLOBAL_ACCESS_CACHE_MAX_AGE_MS =
  5 * 60 * 1000;

const ALL_BOOK_LEVELS: WebsiteAccessLevel[] = [
  "A1",
  "A2",
  "B1",
  "B2",
  "C1",
  "C2",
];

const ALL_COURSE_LEVELS: WebsiteAccessLevel[] = [
  "A1",
  "A2",
  "B1",
  "B2",
];

const EMPTY_ACCESS: WebsiteAccessSnapshot = {
  checked: false,
  loading: false,
  hasAnyAccess: false,
  hasFullBookAccess: false,
  hasFullCourseAccess: false,
  accessibleBookLevels: [],
  accessibleCourseLevels: [],
  activeEntitlements: [],
  activeProducts: [],
  source: "none",
  error: "",
  lastCheckedAt: 0,
};

const normalizeEmail = (
  value?: string | null,
): string => {
  return String(value || "")
    .trim()
    .toLowerCase();
};

const normalizeId = (
  value?: string | null,
): string => {
  return String(value || "").trim();
};

const normalizeStringList = (
  value: unknown,
): string[] => {
  return Array.isArray(value)
    ? value
        .map((item) =>
          String(item || "").trim(),
        )
        .filter(Boolean)
    : [];
};

const normalizeLevel = (
  value: unknown,
): WebsiteAccessLevel | null => {
  const cleanValue = String(value || "")
    .trim()
    .toUpperCase();

  return ALL_BOOK_LEVELS.includes(
    cleanValue as WebsiteAccessLevel,
  )
    ? (cleanValue as WebsiteAccessLevel)
    : null;
};

const normalizeLevelList = (
  value: unknown,
): WebsiteAccessLevel[] => {
  const values = new Set(
    normalizeStringList(value)
      .map((item) =>
        normalizeLevel(item),
      )
      .filter(
        (
          item,
        ): item is WebsiteAccessLevel =>
          Boolean(item),
      ),
  );

  return ALL_BOOK_LEVELS.filter(
    (level) => values.has(level),
  );
};

const mapToLevels = (
  map: BackendAccessMap | undefined,
  allowedLevels: WebsiteAccessLevel[],
): WebsiteAccessLevel[] => {
  if (!map) {
    return [];
  }

  if (map.full) {
    return [...allowedLevels];
  }

  return allowedLevels.filter(
    (level) => Boolean(map[level]),
  );
};

const hasExactValue = (
  values: string[],
  expected: string,
): boolean => {
  const cleanExpected = expected
    .trim()
    .toLowerCase();

  return values.some(
    (value) =>
      value.trim().toLowerCase() ===
      cleanExpected,
  );
};

const hasFullLevel = (
  value: unknown,
): boolean => {
  return normalizeStringList(value).some(
    (item) =>
      item.trim().toLowerCase() ===
      "full",
  );
};

const uniqueLevels = (
  allowedLevels: WebsiteAccessLevel[],
  ...groups: WebsiteAccessLevel[][]
): WebsiteAccessLevel[] => {
  const values = new Set(groups.flat());

  return allowedLevels.filter(
    (level) => values.has(level),
  );
};

const parseAccessResponse = (
  data: BackendAccessResponse,
): WebsiteAccessSnapshot => {
  const activeEntitlements =
    normalizeStringList(
      data.revenuecat_active_entitlements,
    );

  const activeProducts =
    normalizeStringList(
      data.revenuecat_active_products,
    );

  const hasPremiumEntitlement =
    hasExactValue(
      activeEntitlements,
      WEBSITE_PREMIUM_ENTITLEMENT,
    ) ||
    hasExactValue(
      activeEntitlements,
      MOBILE_PREMIUM_ENTITLEMENT,
    );

  const hasFullBookAccess = Boolean(
    data.has_full_book_access ||
      data.book_access_map?.full ||
      hasFullLevel(
        data.book_access_levels,
      ) ||
      hasPremiumEntitlement,
  );

  const hasFullCourseAccess = Boolean(
    data.has_full_course_access ||
      data.course_access_map?.full ||
      data.access_map?.full ||
      hasFullLevel(
        data.course_access_levels,
      ),
  );

  const accessibleBookLevels =
    hasFullBookAccess
      ? [...ALL_BOOK_LEVELS]
      : uniqueLevels(
          ALL_BOOK_LEVELS,

          normalizeLevelList(
            data.accessible_book_levels,
          ),

          mapToLevels(
            data.book_access_map,
            ALL_BOOK_LEVELS,
          ),
        );

  const accessibleCourseLevels =
    hasFullCourseAccess
      ? [...ALL_COURSE_LEVELS]
      : uniqueLevels(
          ALL_COURSE_LEVELS,

          normalizeLevelList(
            data.accessible_course_levels,
          ),

          normalizeLevelList(
            data.accessible_levels,
          ),

          mapToLevels(
            data.course_access_map ||
              data.access_map,
            ALL_COURSE_LEVELS,
          ),
        );

  const hasAnyAccess = Boolean(
    data.has_record ||
      hasFullBookAccess ||
      hasFullCourseAccess ||
      accessibleBookLevels.length > 0 ||
      accessibleCourseLevels.length > 0,
  );

  return {
    checked: true,
    loading: false,
    hasAnyAccess,
    hasFullBookAccess,
    hasFullCourseAccess,
    accessibleBookLevels,
    accessibleCourseLevels,
    activeEntitlements,
    activeProducts,
    source: String(
      data.source || "unknown",
    ),

    error:
      typeof data.revenuecat_error ===
      "string"
        ? data.revenuecat_error
        : "",

    lastCheckedAt: Date.now(),
  };
};

const getGlobalAccessCacheKey = (
  email: string,
  userId: string,
): string => {
  return `${GLOBAL_ACCESS_CACHE_PREFIX}:${normalizeEmail(
    email,
  )}:${normalizeId(userId)}`;
};

const readStorageItem = (
  storage: Storage,
  key: string,
): StoredAccessCache | null => {
  try {
    const raw = storage.getItem(key);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(
      raw,
    ) as StoredAccessCache;

    if (
      !parsed?.access ||
      !parsed?.savedAt ||
      !parsed?.email ||
      !parsed?.userId
    ) {
      storage.removeItem(key);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

const readGlobalAccessCache = (
  email: string,
  userId: string,
): {
  entry: StoredAccessCache | null;
  isFresh: boolean;
} => {
  const cleanEmail =
    normalizeEmail(email);

  const cleanUserId =
    normalizeId(userId);

  if (
    !cleanEmail ||
    !cleanUserId
  ) {
    return {
      entry: null,
      isFresh: false,
    };
  }

  const key =
    getGlobalAccessCacheKey(
      cleanEmail,
      cleanUserId,
    );

  let entry:
    | StoredAccessCache
    | null = null;

  try {
    entry = readStorageItem(
      window.sessionStorage,
      key,
    );

    if (!entry) {
      entry = readStorageItem(
        window.localStorage,
        key,
      );
    }
  } catch {
    entry = null;
  }

  if (
    !entry ||
    normalizeEmail(entry.email) !==
      cleanEmail ||
    normalizeId(entry.userId) !==
      cleanUserId
  ) {
    return {
      entry: null,
      isFresh: false,
    };
  }

  return {
    entry,

    isFresh:
      Date.now() -
        entry.savedAt <=
      GLOBAL_ACCESS_CACHE_MAX_AGE_MS,
  };
};

const writeGlobalAccessCache = (
  email: string,
  userId: string,
  access: WebsiteAccessSnapshot,
): void => {
  const cleanEmail =
    normalizeEmail(email);

  const cleanUserId =
    normalizeId(userId);

  if (
    !cleanEmail ||
    !cleanUserId
  ) {
    return;
  }

  const key =
    getGlobalAccessCacheKey(
      cleanEmail,
      cleanUserId,
    );

  const savedAt =
    access.lastCheckedAt ||
    Date.now();

  const payload: StoredAccessCache = {
    userId: cleanUserId,
    email: cleanEmail,
    savedAt,

    access: {
      ...access,
      checked: true,
      loading: false,
      lastCheckedAt: savedAt,
    },
  };

  const serialized =
    JSON.stringify(payload);

  try {
    window.sessionStorage.setItem(
      key,
      serialized,
    );
  } catch {
    // sessionStorage is optional.
  }

  try {
    window.localStorage.setItem(
      key,
      serialized,
    );
  } catch {
    // localStorage is optional.
  }
};

const clearGlobalAccessCaches =
  (): void => {
    const clearStorage = (
      storage: Storage,
    ): void => {
      try {
        Object.keys(storage).forEach(
          (key) => {
            if (
              key.startsWith(
                `${GLOBAL_ACCESS_CACHE_PREFIX}:`,
              )
            ) {
              storage.removeItem(key);
            }
          },
        );
      } catch {
        // Storage is optional.
      }
    };

    try {
      clearStorage(
        window.sessionStorage,
      );

      clearStorage(
        window.localStorage,
      );
    } catch {
      // Browser storage can be blocked.
    }
  };

const dispatchSharedAuthState = (
  user: User | null,
): void => {
  const detail: AuthChangeDetail = {
    isLoggedIn: Boolean(user),

    username:
      user?.displayName ||
      user?.email ||
      undefined,

    uid: user?.uid,
    email: user?.email,
  };

  try {
    if (user) {
      window.localStorage.setItem(
        SHARED_AUTH_KEY,
        "true",
      );

      window.localStorage.setItem(
        SHARED_AUTH_USER_KEY,

        user.displayName ||
          user.email ||
          user.uid,
      );
    } else {
      window.localStorage.removeItem(
        SHARED_AUTH_KEY,
      );

      window.localStorage.removeItem(
        SHARED_AUTH_USER_KEY,
      );
    }
  } catch {
    // localStorage can be blocked.
  }

  window.dispatchEvent(
    new CustomEvent<AuthChangeDetail>(
      SHARED_AUTH_EVENT,
      {
        detail,
      },
    ),
  );
};

const dispatchAccessState = (
  access: WebsiteAccessSnapshot,
): void => {
  window.dispatchEvent(
    new CustomEvent<WebsiteAccessSnapshot>(
      GLOBAL_ACCESS_EVENT,
      {
        detail: access,
      },
    ),
  );

  window.dispatchEvent(
    new CustomEvent(
      PROFILE_ACCESS_EVENT,
      {
        detail: {
          hasWebsitePremium:
            access.hasFullBookAccess,
        },
      },
    ),
  );
};

const getFriendlyFirebaseError = (
  error: unknown,
): string => {
  const code =
    typeof error === "object" &&
    error !== null &&
    "code" in error
      ? String(
          (
            error as {
              code?: string;
            }
          ).code,
        )
      : "";

  switch (code) {
    case "auth/email-already-in-use":
      return "Diese E-Mail-Adresse ist bereits registriert.";

    case "auth/invalid-email":
      return "Bitte geben Sie eine gültige E-Mail-Adresse ein.";

    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "E-Mail oder Passwort ist nicht korrekt.";

    case "auth/weak-password":
      return "Das Passwort muss mindestens 6 Zeichen lang sein.";

    case "auth/network-request-failed":
      return "Netzwerkfehler. Bitte prüfen Sie Ihre Internetverbindung.";

    default:
      return "Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.";
  }
};

export const AuthProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const [user, setUser] =
    useState<User | null>(null);

  const [
    authLoading,
    setAuthLoading,
  ] =
    useState<boolean>(true);

  const [
    authError,
    setAuthError,
  ] =
    useState<string>("");

  const [access, setAccess] =
    useState<WebsiteAccessSnapshot>({
      ...EMPTY_ACCESS,
      loading: true,
    });

  const accessRef =
    useRef<WebsiteAccessSnapshot>({
      ...EMPTY_ACCESS,
      loading: true,
    });

  const accessOwnerRef =
    useRef<string>("");

  const accessRequestIdRef =
    useRef<number>(0);

  const accessRequestRef =
    useRef<AccessRequest | null>(
      null,
    );

  const updateAccess =
    useCallback(
      (
        nextAccess:
          WebsiteAccessSnapshot,

        ownerUserId = "",
      ): void => {
        accessRef.current =
          nextAccess;

        accessOwnerRef.current =
          ownerUserId;

        setAccess(nextAccess);

        dispatchAccessState(
          nextAccess,
        );
      },
      [],
    );

  const loadAccessForUser =
    useCallback(
      async (
        targetUser: User | null,
        forceRefresh = false,
      ): Promise<WebsiteAccessSnapshot> => {
        const cleanEmail =
          normalizeEmail(
            targetUser?.email,
          );

        const cleanUserId =
          normalizeId(
            targetUser?.uid,
          );

        if (
          !targetUser ||
          !cleanEmail ||
          !cleanUserId
        ) {
          const emptyAccess: WebsiteAccessSnapshot =
            {
              ...EMPTY_ACCESS,
              checked: true,
              loading: false,
            };

          updateAccess(
            emptyAccess,
            "",
          );

          return emptyAccess;
        }

        const currentAccess =
          accessRef.current;

        const currentAccessIsFresh =
          Boolean(
            accessOwnerRef.current ===
              cleanUserId &&
              currentAccess.checked &&
              currentAccess.lastCheckedAt >
                0 &&
              Date.now() -
                currentAccess.lastCheckedAt <=
                GLOBAL_ACCESS_CACHE_MAX_AGE_MS,
          );

        if (
          !forceRefresh &&
          currentAccessIsFresh
        ) {
          return currentAccess;
        }

        const cached =
          readGlobalAccessCache(
            cleanEmail,
            cleanUserId,
          );

        /**
         * Fresh cache:
         * return immediately without contacting Laravel.
         */
        if (
          !forceRefresh &&
          cached.entry &&
          cached.isFresh
        ) {
          const cachedAccess: WebsiteAccessSnapshot =
            {
              ...cached.entry.access,
              checked: true,
              loading: false,
              lastCheckedAt:
                cached.entry.savedAt,
            };

          updateAccess(
            cachedAccess,
            cleanUserId,
          );

          return cachedAccess;
        }

        /**
         * Expired cache:
         * keep showing the saved access while one refresh runs.
         */
        if (
          cached.entry &&
          !forceRefresh
        ) {
          updateAccess(
            {
              ...cached.entry.access,
              checked: true,
              loading: true,
              error: "",
              lastCheckedAt:
                cached.entry.savedAt,
            },

            cleanUserId,
          );
        } else {
          updateAccess(
            {
              ...currentAccess,
              loading: true,
              error: "",
            },

            cleanUserId,
          );
        }

        const requestKey =
          `${cleanUserId}:${cleanEmail}`;

        if (
          accessRequestRef.current
            ?.key === requestKey
        ) {
          return accessRequestRef.current
            .promise;
        }

        const requestId =
          accessRequestIdRef.current +
          1;

        accessRequestIdRef.current =
          requestId;

        const requestPromise =
          (async (): Promise<WebsiteAccessSnapshot> => {
            try {
              const query =
                new URLSearchParams({
                  email:
                    cleanEmail,

                  app_user_id:
                    cleanUserId,

                  force_refresh:
                    "1",

                  access_refresh:
                    String(
                      Date.now(),
                    ),
                });

              const response =
                await fetch(
                  `${API_BASE_URL}/api/access/check?${query.toString()}`,

                  {
                    method: "GET",
                    cache: "no-store",

                    headers: {
                      Accept:
                        "application/json",

                      "Cache-Control":
                        "no-cache",

                      Pragma:
                        "no-cache",
                    },
                  },
                );

              const json =
                (await response
                  .json()
                  .catch(
                    () => ({}),
                  )) as BackendAccessResponse;

              if (!response.ok) {
                throw new Error(
                  json.message ||
                    json.error ||
                    `Der Zugang konnte nicht geprüft werden. Status: ${response.status}`,
                );
              }

              const nextAccess =
                parseAccessResponse(
                  json,
                );

              if (
                accessRequestIdRef
                  .current !==
                requestId
              ) {
                return nextAccess;
              }

              updateAccess(
                nextAccess,
                cleanUserId,
              );

              writeGlobalAccessCache(
                cleanEmail,
                cleanUserId,
                nextAccess,
              );

              return nextAccess;
            } catch (error) {
              const message =
                error instanceof Error
                  ? error.message
                  : "Der Zugang konnte nicht geprüft werden.";

              const fallbackEntry =
                readGlobalAccessCache(
                  cleanEmail,
                  cleanUserId,
                ).entry;

              const failedAccess: WebsiteAccessSnapshot =
                fallbackEntry
                  ? {
                      ...fallbackEntry.access,
                      checked: true,
                      loading: false,
                      error: message,
                      lastCheckedAt:
                        fallbackEntry.savedAt,
                    }
                  : {
                      ...EMPTY_ACCESS,
                      checked: true,
                      loading: false,
                      error: message,
                    };

              if (
                accessRequestIdRef
                  .current ===
                requestId
              ) {
                updateAccess(
                  failedAccess,
                  cleanUserId,
                );
              }

              return failedAccess;
            } finally {
              if (
                accessRequestRef.current
                  ?.key === requestKey
              ) {
                accessRequestRef.current =
                  null;
              }
            }
          })();

        accessRequestRef.current = {
          key: requestKey,
          promise: requestPromise,
        };

        return requestPromise;
      },
      [updateAccess],
    );

  const refreshAccess =
    useCallback(
      async (
        forceRefresh = false,
      ): Promise<WebsiteAccessSnapshot> => {
        return loadAccessForUser(
          firebaseAuth.currentUser,
          forceRefresh,
        );
      },
      [loadAccessForUser],
    );

  useEffect(() => {
    const unsubscribe =
      onAuthStateChanged(
        firebaseAuth,

        (firebaseUser) => {
          setUser(firebaseUser);
          setAuthLoading(false);

          dispatchSharedAuthState(
            firebaseUser,
          );

          if (!firebaseUser) {
            accessRequestIdRef.current +=
              1;

            accessRequestRef.current =
              null;

            clearGlobalAccessCaches();

            const emptyAccess: WebsiteAccessSnapshot =
              {
                ...EMPTY_ACCESS,
                checked: true,
                loading: false,
              };

            updateAccess(
              emptyAccess,
              "",
            );

            return;
          }

          /**
           * false means:
           * use the five-minute cache first.
           *
           * A fresh cache does not contact Laravel.
           */
          void loadAccessForUser(
            firebaseUser,
            false,
          );
        },
      );

    return unsubscribe;
  }, [
    loadAccessForUser,
    updateAccess,
  ]);

  useEffect(() => {
    const handleVisibilityChange =
      (): void => {
        if (
          document.visibilityState !==
            "visible" ||
          !firebaseAuth.currentUser
        ) {
          return;
        }

        /**
         * Also reuses cache.
         * Fetches only when cache expired.
         */
        void refreshAccess(false);
      };

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
    );

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );
    };
  }, [refreshAccess]);

  const login = async (
    email: string,
    password: string,
  ): Promise<boolean> => {
    setAuthError("");

    try {
      await signInWithEmailAndPassword(
        firebaseAuth,
        email
          .trim()
          .toLowerCase(),
        password,
      );

      return true;
    } catch (error) {
      setAuthError(
        getFriendlyFirebaseError(
          error,
        ),
      );

      return false;
    }
  };

  const register = async (
    email: string,
    password: string,
    displayName?: string,
  ): Promise<boolean> => {
    setAuthError("");

    try {
      const credential =
        await createUserWithEmailAndPassword(
          firebaseAuth,

          email
            .trim()
            .toLowerCase(),

          password,
        );

      if (
        displayName?.trim()
      ) {
        await updateProfile(
          credential.user,

          {
            displayName:
              displayName.trim(),
          },
        );
      }

      dispatchSharedAuthState(
        credential.user,
      );

      return true;
    } catch (error) {
      setAuthError(
        getFriendlyFirebaseError(
          error,
        ),
      );

      return false;
    }
  };

  const logout =
    async (): Promise<void> => {
      setAuthError("");

      accessRequestIdRef.current +=
        1;

      accessRequestRef.current =
        null;

      clearGlobalAccessCaches();

      await signOut(firebaseAuth);

      dispatchSharedAuthState(null);
    };

  const clearAuthError =
    (): void => {
      setAuthError("");
    };

  const canAccessBookLevel =
    useCallback(
      (
        level: WebsiteAccessLevel,
      ): boolean => {
        return (
          access.hasFullBookAccess ||
          access.accessibleBookLevels.includes(
            level,
          )
        );
      },
      [
        access.accessibleBookLevels,
        access.hasFullBookAccess,
      ],
    );

  const canAccessCourseLevel =
    useCallback(
      (
        level: WebsiteAccessLevel,
      ): boolean => {
        return (
          access.hasFullCourseAccess ||
          access.accessibleCourseLevels.includes(
            level,
          )
        );
      },
      [
        access.accessibleCourseLevels,
        access.hasFullCourseAccess,
      ],
    );

  const value =
    useMemo<AuthContextValue>(
      () => ({
        user,

        userId:
          user?.uid ?? "",

        userEmail:
          user?.email ?? "",

        isLoggedIn:
          Boolean(user),

        authLoading,
        authError,

        accessChecked:
          access.checked,

        accessLoading:
          access.loading,

        accessError:
          access.error,

        accessLastCheckedAt:
          access.lastCheckedAt,

        accessSource:
          access.source,

        hasAnyAccess:
          access.hasAnyAccess,

        hasFullBookAccess:
          access.hasFullBookAccess,

        hasFullCourseAccess:
          access.hasFullCourseAccess,

        accessibleBookLevels:
          access.accessibleBookLevels,

        accessibleCourseLevels:
          access.accessibleCourseLevels,

        activeEntitlements:
          access.activeEntitlements,

        activeProducts:
          access.activeProducts,

        login,
        register,
        logout,
        clearAuthError,
        refreshAccess,
        canAccessBookLevel,
        canAccessCourseLevel,
      }),
      [
        access,
        authError,
        authLoading,
        canAccessBookLevel,
        canAccessCourseLevel,
        refreshAccess,
        user,
      ],
    );

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth =
  (): AuthContextValue => {
    const context =
      useContext(AuthContext);

    if (!context) {
      throw new Error(
        "useAuth must be used inside AuthProvider.",
      );
    }

    return context;
  };